import { AppRoute, buildRoute } from 'appRoutes'
import {
  FRAUD_ANALYSIS_CLIENT_TYPES,
  FRAUD_ANALYSIS_DEFAULT_CLIENT_TYPE,
  getFraudScoreLabels,
  sortFraudScoreLabels,
  UploadLabelButtons,
  UploadLabelButtonsLabel,
  useFraudScoreLabelDisplay,
} from 'features/fraudAnalysis'
import { ClientType } from 'models'
import { IdEventV4 } from 'models/event'
import { useMemo } from 'react'

import { CellWrapper } from './CellAction'
import { columnHelper } from './columnHelper'
import { Unset } from './MissingData'

export const FRAUD_SCORE_COLUMN_ID = 'fraudScore'

/** The model client type matching the event's SDK, so the label links to the model that scored it. */
function getEventClientType(event: IdEventV4): ClientType {
  const platform = event.sdk?.platform
  return FRAUD_ANALYSIS_CLIENT_TYPES.find((clientType) => clientType === platform) ?? FRAUD_ANALYSIS_DEFAULT_CLIENT_TYPE
}

function FraudScoreLabelsCell({ event, workspaceId }: { event: IdEventV4; workspaceId: string }) {
  const { getLabelName } = useFraudScoreLabelDisplay(workspaceId)

  // TODO(DASH-3148): once label filters exist, show the labels selected in the filters first.
  const labels = useMemo<UploadLabelButtonsLabel[]>(() => {
    const clientType = getEventClientType(event)
    return sortFraudScoreLabels(getFraudScoreLabels(event))
      .filter(({ prediction }) => prediction)
      .map(({ label, category }) => ({
        labelSlug: label,
        displayName: getLabelName(label),
        category: category ? { categorySlug: category } : null,
        modelHref: buildRoute(AppRoute.FraudAnalysisModelDetailsOverview, {
          subscriptionId: workspaceId,
          labelSlug: label,
          clientType,
        }),
      }))
  }, [event, getLabelName, workspaceId])

  if (labels.length === 0) {
    return <Unset />
  }

  // Events table cells bleed, so the cell pads itself like the other columns and lines up with the header
  return (
    <CellWrapper>
      <UploadLabelButtons labels={labels} />
    </CellWrapper>
  )
}

/** Fraud Score labels detected on the event (`prediction: true`), like the dataset uploads table's labels. */
export const fraudScoreColumn = ({ workspaceId }: { workspaceId: string }) =>
  columnHelper.display({
    id: FRAUD_SCORE_COLUMN_ID,
    header: 'Fraud Score',
    cell: ({ row }) => <FraudScoreLabelsCell event={row.original} workspaceId={workspaceId} />,
    meta: { className: 'min-w-56 max-w-56', align: 'end' },
  })
